import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Inbox, Settings } from "lucide-react";
import { api, clearToken, getToken } from "@/lib/auth";
import { BrandLogo } from "@/components/BrandLogo";

const STATUS = ["new", "contacted", "closed"];
const badge = {
  new: "text-[#d4af37] border-[#d4af37]/40",
  contacted: "text-[#7fb0ff] border-[#7fb0ff]/40",
  closed: "text-[#737373] border-white/15",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0 });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const logout = useCallback(() => { clearToken(); nav("/admin/login"); }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/admin/leads"), api.get("/admin/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); return; }
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked ${status}`);
      const s = await api.get("/admin/stats"); setStats(s.data);
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      setActive(null);
      toast.success("Lead deleted");
    } catch { toast.error("Delete failed"); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  let body;
  if (loading) {
    body = <div className="py-24 text-center mono text-xs uppercase tracking-[0.2em] text-[#737373]">Loading…</div>;
  } else if (leads.length === 0) {
    body = (
      <div data-testid="empty-state" className="py-24 flex flex-col items-center text-[#737373]">
        <Inbox size={40} className="mb-4" />
        <p className="mono text-xs uppercase tracking-[0.2em]">No leads yet</p>
      </div>
    );
  } else {
    body = (
      <div data-testid="leads-table" className="border border-white/10 rounded-sm overflow-hidden">
        {leads.map((l) => (
          <motion.div
            key={l.id}
            data-testid={`lead-row-${l.id}`}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="grid grid-cols-[1.4fr_1fr_1fr_auto] gap-4 items-center px-6 py-5 border-b border-white/10 last:border-0 hover:bg-white/[0.02] transition-colors cursor-pointer"
            onClick={() => setActive(active === l.id ? null : l.id)}
          >
            <div className="min-w-0">
              <div className="text-[#f4f0ea] truncate">{l.name}</div>
              <div className="text-[#737373] text-sm truncate">{l.email}</div>
            </div>
            <div className="text-[#a3a3a3] text-sm">{l.situation || l.coverage || "—"}</div>
            <div className="text-[#737373] text-sm mono">{fmt(l.created_at)}</div>
            <span className={`mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border rounded-full ${badge[l.status] || badge.new}`}>{l.status}</span>

            {active === l.id && (
              <div className="col-span-4 pt-5 mt-2 border-t border-white/10" onClick={(e) => e.stopPropagation()}>
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3 mb-6">
                  {[["Situation", l.situation], ["Phone", l.phone], ["Age", l.age], ["Amount", l.coverage_amount], ["Message", l.message]].map(([k, v]) => (
                    <div key={k}>
                      <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#737373] mb-1">{k}</div>
                      <div className="text-[#f4f0ea]/90 text-sm">{v || "—"}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {STATUS.map((s) => (
                    <button
                      key={s}
                      data-testid={`set-status-${s}-${l.id}`}
                      onClick={() => updateStatus(l.id, s)}
                      className={`mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border rounded-full transition-colors ${l.status === s ? "border-[#d4af37] text-[#d4af37]" : "border-white/15 text-[#a3a3a3] hover:text-[#f4f0ea]"}`}
                    >{s}</button>
                  ))}
                  <a href={`mailto:${l.email}`} className="mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 bg-[#f4f0ea] text-[#030303] rounded-full hover:bg-[#d4af37]">Reply</a>
                  <button data-testid={`delete-${l.id}`} onClick={() => remove(l.id)} className="ml-auto text-[#737373] hover:text-red-400 flex items-center gap-2 mono text-[10px] uppercase tracking-[0.2em]">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen grain bg-[#030303] text-[#f4f0ea]">
      <header className="sticky top-0 z-40 bg-[#030303]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BrandLogo size="sm" />
            <span className="hidden sm:inline-block h-6 w-px bg-white/10" />
            <span className="hidden sm:inline mono text-[9px] uppercase tracking-[0.3em] text-[#d4af37]">JCI Admin Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="refresh-btn" onClick={load} className="mono text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#f4f0ea] flex items-center gap-2">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button data-testid="settings-btn" onClick={() => nav("/admin/settings")} className="mono text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#d4af37] flex items-center gap-2">
              <Settings size={13} /> Settings
            </button>
            <button data-testid="logout-btn" onClick={logout} className="mono text-[10px] uppercase tracking-[0.2em] px-5 py-3 border border-white/15 rounded-full hover:border-[#d4af37]/50 hover:text-[#d4af37] flex items-center gap-2">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
        {/* stats */}
        <div className="grid grid-cols-3 gap-4 mb-12">
          {[["Total leads", stats.total], ["New", stats.new], ["Contacted", stats.contacted]].map(([label, val]) => (
            <div key={label} data-testid={`stat-${label.split(" ")[0].toLowerCase()}`} className="bg-[#0a0a0a] border border-white/10 rounded-sm p-6 lg:p-8">
              <div className="serif text-4xl lg:text-5xl text-[#d4af37] mb-2">{val}</div>
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">{label}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-6">
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">All Requests</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        {body}
      </main>
    </div>
  );
}
